import { Link } from 'react-router-dom';
import { PageFrame } from '../components/PageFrame';

// eslint-disable-next-line react-refresh/only-export-components -- shared state contract belongs with its renderer
export const stateContent = {
  loading: ['Loading', 'Your work is unchanged.', 'Please wait.'],
  'first-use-empty': [
    'Nothing here yet',
    'There is no work to preserve.',
    'Use the approved start action when it becomes available.',
  ],
  'filtered-empty': [
    'No matching results',
    'Your work is preserved.',
    'Change or clear the filters.',
  ],
  'validation-error': [
    'Some details need attention',
    'Your entries are preserved.',
    'Review the highlighted fields.',
  ],
  'system-error': [
    'DodoSkills is temporarily unavailable',
    'Your work is preserved where possible.',
    'Try again later or return safely.',
  ],
  'permission-denied': [
    'Access not permitted',
    'No work was changed.',
    'Return to your authorised workspace.',
  ],
  offline: [
    'You are offline',
    'Saved work is preserved on this device where permitted.',
    'Reconnect and try again.',
  ],
  success: [
    'Action completed',
    'Your work was saved.',
    'Continue to the permitted next step.',
  ],
  'recoverable-conflict': [
    'This work changed elsewhere',
    'Your version is preserved.',
    'Review the latest version before continuing.',
  ],
  'expired-access': [
    'Access has expired',
    'Completed work is preserved.',
    'Ask an authorised adult for new temporary access.',
  ],
  'revoked-connection': [
    'Connection ended',
    'Each party keeps records they are lawfully entitled to retain.',
    'Return to your own workspace.',
  ],
  'wrong-school': [
    'This is not the expected school workspace',
    'No work was changed.',
    'Return and choose the authorised school.',
  ],
  'not-found': [
    'Page not found',
    'No work was changed.',
    'Return to a known DodoSkills page.',
  ],
  archived: [
    'This item is archived',
    'Existing work is preserved.',
    'Return to the active workspace.',
  ],
  'privacy-safety-stop': [
    'Stopped to protect privacy and safety',
    'Permitted work is preserved.',
    'Return safely or contact an authorised adult.',
  ],
} as const;

export type StateName = keyof typeof stateContent;

export function StatePage({ state }: { state: StateName }) {
  const [title, preservation, action] = stateContent[state];
  const liveRole =
    state === 'loading' || state === 'success' ? 'status' : 'alert';
  return (
    // Every one of these says "Return to ..." -- the Back / Dashboard row
    // makes that an actual control instead of a dead end. Role selection
    // routes a signed-in adult to their own workspace (and a signed-out one
    // to sign-in).
    <PageFrame
      title={title}
      audience="adult"
      dashboardHref="/adult/role-selection"
    >
      <section
        role={liveRole}
        aria-live="polite"
        className="mt-6 max-w-2xl rounded-card border border-line bg-white p-6 shadow-card"
      >
        <h2 className="font-bold">What happened</h2>
        <p>{preservation}</p>
        <h2 className="mt-4 font-bold">Next action</h2>
        <p>{action}</p>
        {/* A wrong or old address has no workspace to go back to, so give a
            plain way out (tester report 2026-10-03). */}
        {state === 'not-found' && (
          <Link
            to="/"
            className="mt-4 inline-flex min-h-11 items-center rounded-lg bg-forest-800 px-4 font-semibold text-white hover:bg-forest-950"
          >
            Go to the homepage
          </Link>
        )}
      </section>
    </PageFrame>
  );
}
